// Atributos sugeridos para las variantes, según el rubro de la marca.
//
// ADR-2026-09-06-lanzamientos-atributos-sugeridos. Un atributo es lo que
// diferencia una variante de otra, y qué diferencia a dos productos depende del
// rubro: en moda es el talle, en hogar el acabado, en belleza el tono. Escribirlo
// a mano cada vez es trabajo repetido y produce nombres inconsistentes ("Talle",
// "talles", "Tamaño") que después se ven en la tienda.
//
// Es solo una sugerencia: el campo sigue siendo libre y se puede escribir
// cualquier cosa.

/** Cuando no se reconoce el rubro. Sirve para casi cualquier catálogo. */
export const ATRIBUTOS_GENERICOS = ["Color", "Tamaño", "Material"] as const;

/**
 * Por rubro. Las claves son la forma NORMALIZADA (sin mayúsculas ni acentos) de
 * los valores reales de `brand_profiles.rubro`, que salen del select del
 * onboarding.
 *
 * El orden importa: el primero es el que más se usa en ese rubro y es el que la
 * UI muestra primero.
 */
const POR_RUBRO: Record<string, readonly string[]> = {
  "moda e indumentaria": ["Color", "Talle", "Material", "Estampado"],
  "hogar y decoracion": ["Color", "Medida", "Material", "Acabado"],
  "belleza y cuidado personal": ["Tono", "Tamaño", "Aroma", "Formato"],
  "salud y bienestar": ["Sabor", "Tamaño", "Presentacion"],
  gastronomia: ["Sabor", "Tamaño", "Peso"],
  tecnologia: ["Color", "Capacidad", "Modelo"],
  "arte y manualidades": ["Color", "Tamaño", "Material"],
  jugueteria: ["Color", "Tamaño", "Edad"],
  automotriz: ["Color", "Modelo", "Medida"],
  libreria: ["Color", "Tamaño", "Formato"],
};

/**
 * Rubros tipeados a mano que caen claramente en uno conocido. Salen de los
 * valores reales de la base: `rubro` es texto libre y hay una cola larga
 * ("Lenceria", "Joyeria", "Marroquineria", "electrodomesticos"…).
 *
 * Se buscan como SUBCADENA de la forma normalizada, así que "Lencería y
 * maquillajes" también entra por "lenceria".
 */
const PALABRAS_CLAVE: ReadonlyArray<readonly [string, string]> = [
  ["lenceria", "moda e indumentaria"],
  ["indumentaria", "moda e indumentaria"],
  ["ropa", "moda e indumentaria"],
  ["moda", "moda e indumentaria"],
  ["calzado", "moda e indumentaria"],
  ["zapat", "moda e indumentaria"],
  ["marroquineria", "moda e indumentaria"],
  ["joyeria", "moda e indumentaria"],
  ["bijou", "moda e indumentaria"],
  ["textil", "moda e indumentaria"],
  ["mueble", "hogar y decoracion"],
  ["deco", "hogar y decoracion"],
  ["hogar", "hogar y decoracion"],
  ["electrodomestico", "hogar y decoracion"],
  ["bazar", "hogar y decoracion"],
  ["belleza", "belleza y cuidado personal"],
  ["cosmetic", "belleza y cuidado personal"],
  ["maquillaje", "belleza y cuidado personal"],
  ["perfum", "belleza y cuidado personal"],
  ["gastronom", "gastronomia"],
  ["aliment", "gastronomia"],
  ["tecnolog", "tecnologia"],
  ["juguet", "jugueteria"],
  ["libreria", "libreria"],
];

/** Sin mayúsculas, sin acentos y sin espacios de más. */
function normalizar(v: string): string {
  return v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/**
 * Atributos que se le ofrecen a esta marca, sin los que ya están puestos.
 *
 * A diferencia de los helpers de `lib/rubro` —que comparan EXACTO porque deciden
 * qué modos se ven— acá se normaliza y se buscan palabras clave: una sugerencia
 * de más no rompe nada, y una de menos deja al usuario escribiendo a mano.
 * Nunca devuelve vacío por no reconocer el rubro; sí devuelve vacío si ya se
 * agregaron todas, que es lo correcto.
 */
export function atributosSugeridos(
  rubro: string | null | undefined,
  yaPuestos: readonly string[] = [],
): string[] {
  const clave = normalizar(rubro ?? "");
  let lista: readonly string[] | undefined = clave === "" ? undefined : POR_RUBRO[clave];

  if (!lista && clave !== "") {
    for (const [palabra, destino] of PALABRAS_CLAVE) {
      if (clave.includes(palabra)) {
        lista = POR_RUBRO[destino];
        break;
      }
    }
  }

  const base = lista ?? ATRIBUTOS_GENERICOS;
  const usados = new Set(yaPuestos.map(normalizar).filter((v) => v !== ""));
  const vistos = new Set<string>();
  return base.filter((a) => {
    const n = normalizar(a);
    if (usados.has(n) || vistos.has(n)) return false;
    vistos.add(n);
    return true;
  });
}
